<template>
  <div class="p-8 bg-[#FAFAFA] min-h-screen">
    <header class="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
      <div>
        <h1 class="text-3xl font-extrabold text-[#222222] tracking-tight">{{ t.title }}</h1>
        <p class="text-gray-500 font-medium">{{ t.subtitle }}</p>
      </div>
      <div class="flex gap-3">
        <button class="px-5 py-3 bg-white border border-gray-200 rounded-xl font-bold text-sm hover:bg-gray-50 transition shadow-sm">
          {{ t.scanNetwork }}
        </button>
        <button class="px-5 py-3 bg-[#222222] text-white rounded-xl font-bold text-sm hover:bg-black transition shadow-lg">
          {{ t.pairDevice }}
        </button>
      </div>
    </header>

    <div class="grid xl:grid-cols-2 gap-8">
      
      <div class="space-y-6">
        <div class="flex items-center justify-between px-2">
          <h2 class="text-xl font-black uppercase tracking-widest text-gray-400 text-sm">{{ t.androidKiosks }}</h2>
          <span class="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-[10px] font-bold">12 {{ t.online }}</span>
        </div>

        <div v-for="borne in bornes" :key="borne.id" class="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden hover:border-dimotec-orange transition-all">
          <div class="p-6">
            <div class="flex justify-between items-start mb-6">
              <div class="flex items-center gap-4">
                <div class="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-400">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                </div>
                <div>
                  <h3 class="font-bold text-gray-900">{{ borne.alias }}</h3>
                  <p class="text-xs text-dimotec-orange font-bold uppercase">{{ borne.client }}</p>
                </div>
              </div>
              <div class="flex items-center gap-2 bg-green-50 px-3 py-1 rounded-full border border-green-100">
                <span class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                <span class="text-[10px] font-black text-green-600 uppercase">{{ t.websocketLive }}</span>
              </div>
            </div>

            <div class="grid grid-cols-3 gap-4 border-t border-b border-gray-50 py-6 my-4">
              <div class="text-center">
                <p class="text-[10px] font-bold text-gray-400 uppercase mb-1">{{ t.energy }}</p>
                <div class="flex items-center justify-center gap-1">
                  <svg v-if="borne.onSector" class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 11a2 2 0 10-4 0v2h4v-2z" /></svg>
                  <span class="text-sm font-black">{{ borne.battery }}%</span>
                </div>
              </div>
              <div class="text-center border-l border-r border-gray-50">
                <p class="text-[10px] font-bold text-gray-400 uppercase mb-1">{{ t.printer }}</p>
                <span :class="printerStatusClass(borne.printer)" class="text-[10px] font-bold px-2 py-0.5 rounded">
                  {{ getPrinterText(borne.printer) }}
                </span>
              </div>
              <div class="text-center">
                <p class="text-[10px] font-bold text-gray-400 uppercase mb-1">{{ t.build }}</p>
                <span class="text-sm font-mono font-bold text-gray-600">v{{ borne.version }}</span>
              </div>
            </div>

            <div class="flex gap-2">
              <button class="flex-1 bg-gray-50 py-3 rounded-xl text-xs font-bold hover:bg-gray-100 transition">{{ t.clientLogs }}</button>
              <button class="flex-1 bg-gray-50 py-3 rounded-xl text-xs font-bold hover:bg-gray-100 transition">{{ t.updateApk }}</button>
            </div>
          </div>
        </div>
      </div>

      <div class="space-y-6">
        <div class="flex items-center justify-between px-2">
          <h2 class="text-xl font-black uppercase tracking-widest text-gray-400 text-sm">{{ t.tvDisplay }}</h2>
          <span class="bg-purple-100 text-purple-600 px-3 py-1 rounded-full text-[10px] font-bold">8 {{ t.broadcasts }}</span>
        </div>

        <div v-for="ecran in ecrans" :key="ecran.id" class="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden group">
          <div class="aspect-video bg-gray-900 relative flex items-center justify-center overflow-hidden">
             <div class="absolute inset-0 opacity-40 grayscale-[0.5] blur-[2px] transition-all group-hover:blur-0 group-hover:opacity-100 group-hover:grayscale-0">
               <img :src="ecran.previewUrl" class="w-full h-full object-cover" />
             </div>
             <div class="relative z-10 text-center pointer-events-none">
                <div class="bg-black/60 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
                  <p class="text-white text-[10px] font-bold uppercase tracking-[0.2em]">{{ t.liveRender }} - {{ ecran.fileAssigned }}</p>
                </div>
             </div>
             <div class="absolute inset-0 bg-blue-500/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                <button class="bg-white text-dimotec-orange p-3 rounded-full shadow-xl hover:scale-110 transition">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                </button>
                <button class="bg-white text-dimotec-orange p-3 rounded-full shadow-xl hover:scale-110 transition">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                </button>
             </div>
          </div>

          <div class="p-6">
            <div class="flex justify-between items-center mb-4">
              <div>
                <h3 class="font-bold text-gray-900">{{ ecran.alias }}</h3>
                <p class="text-xs text-gray-400 uppercase font-bold">{{ ecran.client }} — IP: {{ ecran.ip }}</p>
              </div>
              <div class="text-right">
                <p class="text-[10px] font-bold text-gray-400 uppercase mb-1">{{ t.connectedQueue }}</p>
                <span class="text-sm font-black text-dimotec-orange">{{ ecran.fileAssigned }}</span>
              </div>
            </div>

            <div class="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl">
              <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
              </div>
              <p class="text-xs font-bold text-gray-600">{{ t.pairedWith }} : <span class="text-gray-900">{{ t.kiosk }} #042 ({{ t.entrance }})</span></p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      currentLanguage: localStorage.getItem("appLanguage") || "fr",
      translations: {
        fr: {
          title: "Gestion du Parc",
          subtitle: "Monitoring technique en temps réel des bornes et écrans.",
          scanNetwork: "Scanner le réseau",
          pairDevice: "Appairer un nouvel appareil",
          androidKiosks: "Bornes Android (APK)",
          online: "En ligne",
          websocketLive: "WebSocket Live",
          energy: "Énergie",
          printer: "Imprimante",
          build: "Build",
          clientLogs: "Logs Client",
          updateApk: "Update APK",
          tvDisplay: "Affichage TV & Dynamic Render",
          broadcasts: "Diffusions",
          liveRender: "Rendu Live",
          connectedQueue: "File Connectée",
          pairedWith: "Appairé avec",
          kiosk: "Borne",
          entrance: "Entrée",
          ready: "Prête",
          lowPaper: "Papier Bas",
          cutterError: "Erreur Massicot"
        },
        en: {
          title: "Hardware Management",
          subtitle: "Real-time technical monitoring of kiosks and screens.",
          scanNetwork: "Scan network",
          pairDevice: "Pair new device",
          androidKiosks: "Android Kiosks (APK)",
          online: "Online",
          websocketLive: "WebSocket Live",
          energy: "Power",
          printer: "Printer",
          build: "Build",
          clientLogs: "Client Logs",
          updateApk: "Update APK",
          tvDisplay: "TV Display & Dynamic Render",
          broadcasts: "Broadcasts",
          liveRender: "Live Render",
          connectedQueue: "Connected Queue",
          pairedWith: "Paired with",
          kiosk: "Kiosk",
          entrance: "Entrance",
          ready: "Ready",
          lowPaper: "Low Paper",
          cutterError: "Cutter Error"
        },
        de: {
          title: "Hardware-Verwaltung",
          subtitle: "Echtzeit-technische Überwachung von Kiosken und Bildschirmen.",
          scanNetwork: "Netzwerk scannen",
          pairDevice: "Neues Gerät koppeln",
          androidKiosks: "Android-Kioske (APK)",
          online: "Online",
          websocketLive: "WebSocket Live",
          energy: "Energie",
          printer: "Drucker",
          build: "Build",
          clientLogs: "Client-Protokolle",
          updateApk: "APK aktualisieren",
          tvDisplay: "TV-Anzeige & Dynamic Render",
          broadcasts: "Übertragungen",
          liveRender: "Live-Rendering",
          connectedQueue: "Verbundene Warteschlange",
          pairedWith: "Gekoppelt mit",
          kiosk: "Kiosk",
          entrance: "Eingang",
          ready: "Bereit",
          lowPaper: "Wenig Papier",
          cutterError: "Schneidefehler"
        },
        it: {
          title: "Gestione Hardware",
          subtitle: "Monitoraggio tecnico in tempo reale dei chioschi e schermi.",
          scanNetwork: "Scansiona rete",
          pairDevice: "Accoppia nuovo dispositivo",
          androidKiosks: "Chioschi Android (APK)",
          online: "Online",
          websocketLive: "WebSocket Live",
          energy: "Energia",
          printer: "Stampante",
          build: "Build",
          clientLogs: "Log Client",
          updateApk: "Aggiorna APK",
          tvDisplay: "Display TV & Dynamic Render",
          broadcasts: "Trasmissioni",
          liveRender: "Rendering Live",
          connectedQueue: "Coda Collegata",
          pairedWith: "Accoppiato con",
          kiosk: "Chiosco",
          entrance: "Ingresso",
          ready: "Pronta",
          lowPaper: "Carta Bassa",
          cutterError: "Errore Taglierina"
        }
      },
      bornes: [
        { id: 1, alias: "Borne Accueil #042", client: "MEDIA LINK SOLUTIONS", onSector: true, battery: 100, printer: "ready", version: "2.4.1" },
        { id: 2, alias: "Borne Entrée Principale", client: "DIMOTEC DIAGNOSTICS", onSector: false, battery: 64, printer: "lowPaper", version: "2.4.0" },
        { id: 3, alias: "Borne VIP Lounge", client: "PHARMACIE DU SOLEIL", onSector: true, battery: 100, printer: "cutterError", version: "2.3.9" }
      ],
      ecrans: [
        { id: 1, alias: "TV Murale - Zone A", client: "MEDIA LINK SOLUTIONS", ip: "192.168.1.45", fileAssigned: "Caisse Principale", previewUrl: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=400" },
        { id: 2, alias: "Ecran Pilier", client: "DIMOTEC DIAGNOSTICS", ip: "10.0.0.12", fileAssigned: "Service Devis", previewUrl: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=400" }
      ]
    }
  },
  computed: {
    t() {
      return this.translations[this.currentLanguage] || this.translations.fr;
    }
  },
  methods: {
    getPrinterText(status) {
      const statusMap = {
        'ready': this.t.ready,
        'lowPaper': this.t.lowPaper,
        'cutterError': this.t.cutterError
      };
      return statusMap[status] || status;
    },
    printerStatusClass(status) {
      switch(status) {
        case 'ready': return 'bg-green-100 text-green-600';
        case 'lowPaper': return 'bg-blue-100 text-blue-600';
        case 'cutterError': return 'bg-red-100 text-red-600';
        default: return 'bg-gray-100 text-gray-400';
      }
    }
  }
}
</script>